import {BuildEnvironment} from '../server/IServerApi';
import {FastifyRequest} from 'fastify';

interface IMetaData {
  title: string;
  link?: React.DetailedHTMLProps<
    React.LinkHTMLAttributes<HTMLLinkElement>,
    HTMLLinkElement
  >[];
  meta?: React.DetailedHTMLProps<
    React.MetaHTMLAttributes<HTMLMetaElement>,
    HTMLMetaElement
  >[];
  script?: React.DetailedHTMLProps<
    React.ScriptHTMLAttributes<HTMLScriptElement>,
    HTMLScriptElement
  >[];
}

interface IMetaDataConfig {
  onServer: boolean;
  appUrl: string;
  cdnUrl: string;
  facebookAppId: string;
  appleAppStoreId: string;
  androidPackage: string;
  appName: string;
  nativeAppLaunchUrl: string;
  environment: BuildEnvironment;
}

let config: IMetaDataConfig | null = null;

// og:image:width/height describe the image BEFORE a scraper fetches it --
// Facebook/LinkedIn/Slack lay the card out from the declared numbers and crop
// to them -- so they may only be published for an image whose size we actually
// know. The generic share asset is the one image that is always 1200x630
// (verified 2026-07-18: cdn.bidmii.com/media/web_assets/share_logo.png is
// 1200x630, 19,067 bytes). Every other og:image is user-supplied and arbitrary:
// contractor profile photos (getProfileMetaData now sends the real photo -- for
// /user/481 that is an 824x824 square) and the Firestore-driven images
// getUnifiedMetaData passes through. Declaring 1200x630 for those is simply
// false and renders the preview distorted, so the two tags are emitted only for
// the asset they describe; omitted, scrapers measure the image themselves.
const SHARE_LOGO_PATH = 'media/web_assets/share_logo.png';
const SHARE_LOGO_WIDTH = '1200';
const SHARE_LOGO_HEIGHT = '630';

export const initializeMetaData = (configuration: IMetaDataConfig) => {
  config = configuration;
};

export const getMetaData = (
  req: FastifyRequest,
  path: string,
  title?: string,
  description?: string,
  imageUrl?: string,
  json_ld?: string,
  // Site-relative path to declare as canonical instead of this page's own URL,
  // for near-duplicate routes that must stay reachable for humans but should not
  // compete in search -- see getFindContractorMeta. Omit for the normal
  // self-canonical case.
  //
  // A leading slash is stripped before concatenation. config.appUrl already ends
  // in one, and the `path` arg above arrives without (getPath does .slice(1)), so
  // passing "/directory/x" yields "https://bidmii.com//directory/x" -- a URL Google
  // treats as distinct from the single-slash one, which would point the canonical
  // at something that isn't in the sitemap and silently waste the whole exercise.
  // Caught on beta 2026-07-15. Normalised here rather than left to each caller.
  canonicalPath?: string,
) => {
  if (!config) {
    throw new Error('MetaDataUtils not initialized');
  }
  const nativeAppUrl = `${config.nativeAppLaunchUrl}/${path}`;
  return metaDataObject(
    req,
    config.onServer,
    title ?? 'Bidmii | Get it done',
    description ??
      'Bidmii helps you find local contractors with verified reviews, get multiple free quotes, and holds payments in trust.',
    title ?? 'Bidmii | Get it done',
    `${config.appUrl}${path}`,
    imageUrl ?? `${config.cdnUrl}media/web_assets/share_logo.png`,
    config.facebookAppId,
    config.appleAppStoreId,
    config.androidPackage,
    config.appName,
    nativeAppUrl,
    config.environment,
    json_ld,
    canonicalPath
      ? `${config.appUrl}${canonicalPath.replace(/^\//, '')}`
      : undefined,
  );
};

const metaDataObject = (
  req: FastifyRequest,
  onServer: boolean,
  title: string,
  description: string,
  openGraphTitle: string,
  url: string,
  imageUrl: string,
  facebookAppId: string,
  appleAppStoreId: string,
  androidPackage: string,
  appName: string,
  nativeAppUrl: string,
  environment: BuildEnvironment,
  json_ld?: string,
  canonicalOverride?: string,
): IMetaData => {
  const helmetAttribute = onServer ? {'data-rh': 'true'} : {};
  const indexing: React.DetailedHTMLProps<
    React.MetaHTMLAttributes<HTMLMetaElement>,
    HTMLMetaElement
  >[] =
    environment !== 'PRODUCTION'
      ? [
          {
            name: 'robots',
            content: 'noindex',
            ...helmetAttribute,
          },
          {
            name: 'googlebot',
            content: 'noindex',
            ...helmetAttribute,
          },
        ]
      : [];
  // Same shape as `indexing` above: a list that is spread into the meta array
  // below, empty when the tags would not be true. See SHARE_LOGO_PATH.
  const imageDimensions: React.DetailedHTMLProps<
    React.MetaHTMLAttributes<HTMLMetaElement>,
    HTMLMetaElement
  >[] = imageUrl.endsWith(SHARE_LOGO_PATH)
    ? [
        {
          property: 'og:image:width',
          content: SHARE_LOGO_WIDTH,
          ...helmetAttribute,
        },
        {
          property: 'og:image:height',
          content: SHARE_LOGO_HEIGHT,
          ...helmetAttribute,
        },
      ]
    : [];
  // A proxied contractor domain still canonicalises to itself: canonicalOverride
  // is about de-duplicating bidmii.com's own near-identical routes, and must not
  // redirect a Pro Website's search signal off its own domain.
  let canonicalUrl = canonicalOverride ?? url;
  if (canonicalUrl.includes('app.bidmii')) {
    canonicalUrl = canonicalUrl.replace('app.bidmii', 'bidmii');
  } else if (req.headers.proxieddomain) {
    canonicalUrl = `https://${req.headers.proxieddomain}${req.url}`;
  }
  return {
    title: title,
    link: [
      {
        rel: 'canonical',
        href: canonicalUrl,
        ...helmetAttribute,
      },
    ],
    meta: [
      ...indexing,
      {
        name: 'description',
        content: description,
        ...helmetAttribute,
      },
      {
        name: 'apple-itunes-app',
        content: `app-id=${appleAppStoreId}, app-argument=${nativeAppUrl}`,
        ...helmetAttribute,
      },
      {
        property: 'fb:app_id',
        content: facebookAppId,
        ...helmetAttribute,
      },
      {
        property: 'og:title',
        content: openGraphTitle,
        ...helmetAttribute,
      },
      {
        property: 'og:description',
        content: description,
        ...helmetAttribute,
      },
      {
        property: 'og:type',
        content: 'website',
        ...helmetAttribute,
      },
      {
        property: 'og:url',
        content: url,
        ...helmetAttribute,
      },
      {
        property: 'og:image',
        content: imageUrl,
        ...helmetAttribute,
      },
      {
        property: 'og:image:secure_url',
        content: imageUrl,
        ...helmetAttribute,
      },
      ...imageDimensions,
      {
        property: 'al:ios:url',
        content: nativeAppUrl,
        ...helmetAttribute,
      },
      {
        property: 'al:ios:app_store_id',
        content: appleAppStoreId,
        ...helmetAttribute,
      },
      {
        property: 'al:ios:app_name',
        content: appName,
        ...helmetAttribute,
      },
      {
        property: 'al:android:url',
        content: nativeAppUrl,
        ...helmetAttribute,
      },
      {
        property: 'al:android:package',
        content: androidPackage,
        ...helmetAttribute,
      },
      {
        property: 'al:android:app_name',
        content: appName,
        ...helmetAttribute,
      },
      {
        name: 'twitter:title',
        content: title,
        ...helmetAttribute,
      },
      {
        name: 'twitter:image',
        content: imageUrl,
        ...helmetAttribute,
      },
      {
        name: 'twitter:site',
        content: '@bidmii',
        ...helmetAttribute,
      },
      {
        name: 'twitter:card',
        content: 'summary_large_image',
        ...helmetAttribute,
      },
      {
        name: 'twitter:description',
        content: description,
        ...helmetAttribute,
      },
    ],
    script: [
      {
        type: 'application/ld+json',
        src: json_ld,
      },
    ],
  };
};

export const toMetaDataString = (metaData: IMetaData) => {
  let metaString = `<title>${metaData.title}</title>`;
  if (metaData.link) {
    metaString += metaData.link
      .map(
        (link) =>
          `<link ${Object.keys(link)
            .map((k) => `${k}="${(link as any)[k] as string}" `)
            .join('')}/>`,
      )
      .join('');
  }
  if (metaData.meta) {
    metaString += metaData.meta
      .map(
        (meta) =>
          `<meta ${Object.keys(meta)
            .map((k) => `${k}="${(meta as any)[k] as string}" `)
            .join('')}/>`,
      )
      .join('');
  }
  if (metaData.script) {
    metaString += metaData.script
      .map((script) =>
        script &&
        script.src &&
        script.src !== undefined &&
        script.src !== 'undefined'
          ? `<script type="${script.type}">${script.src}</script>`
          : '',
      )
      .join('');
  }
  return metaString;
};
